import type { ReportBlock } from '@/lib/types/report'
import { FigureFrame } from '../primitives/FigureFrame'

interface Row {
  name: string
  pct: number
  score: number
  detail?: string | null
}

interface Data {
  kind: string
  unit: string | null
  rows: Row[]
  credits: Row[]
  other: Row | null
  caption: string
  source: string
}

const BAR_PX = 18 // ≤ 24px mark; the row's leftover height is air

const TONE = {
  burden: 'var(--report-burden)',
  credit: 'var(--report-credit)',
  neutral: 'var(--report-neutral)',
} as const

type Tone = keyof typeof TONE

export function ContributionBar({ block }: { block: ReportBlock }) {
  const d = block.data as unknown as Data
  const entries: { row: Row; tone: Tone }[] = [
    ...d.rows.map((row) => ({ row, tone: 'burden' as const })),
    ...(d.other ? [{ row: d.other, tone: 'neutral' as const }] : []),
    ...d.credits.map((row) => ({ row, tone: 'credit' as const })),
  ]
  if (entries.length === 0) return null

  const maxBurden = Math.max(0, ...entries.filter((e) => e.tone !== 'credit').map((e) => e.row.pct))
  const maxCredit = Math.max(0, ...d.credits.map((c) => Math.abs(c.pct)))
  const span = maxBurden + maxCredit
  const baseline = span > 0 ? (maxCredit / span) * 100 : 0
  const hasCredits = d.credits.length > 0

  return (
    <FigureFrame caption={d.caption} source={d.source}>
      <div className="space-y-1.5" role="img" aria-label={d.caption}>
        {entries.map(({ row, tone }, i) => {
          const widthPct = span > 0 ? (Math.abs(row.pct) / span) * 100 : 0
          const label = row.detail ? `${row.name} — ${row.detail}` : row.name
          return (
            <div
              key={`${tone}-${row.name}-${i}`}
              className="grid grid-cols-[minmax(0,13rem)_1fr_4.5rem] items-center gap-x-3"
              title={`${label}: ${row.score.toPrecision(3)} ${d.unit ?? ''} (${row.pct.toFixed(1)}%)`}
            >
              <div className="truncate text-sm text-foreground">
                {row.name}
                {row.detail && <span className="ml-1.5 text-xs text-muted-foreground">{row.detail}</span>}
              </div>
              <div className="relative" style={{ height: BAR_PX }}>
                {hasCredits && (
                  <div className="absolute inset-y-0 w-px bg-border" style={{ left: `${baseline}%` }} aria-hidden="true" />
                )}
                <div
                  className="absolute inset-y-0"
                  style={
                    tone === 'credit'
                      ? {
                          right: `${100 - baseline}%`,
                          width: `${widthPct}%`,
                          background: TONE[tone],
                          borderRadius: '4px 0 0 4px', // rounded data-end, square at baseline
                        }
                      : {
                          left: `${baseline}%`,
                          width: `${widthPct}%`,
                          background: TONE[tone],
                          borderRadius: '0 4px 4px 0',
                        }
                  }
                />
              </div>
              <div className="text-right text-sm tabular-nums text-foreground">{row.pct.toFixed(1)}%</div>
            </div>
          )
        })}
        {hasCredits && (
          <div className="flex gap-5 pt-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: TONE.burden }} />
              Burden
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: TONE.credit }} />
              Credit (avoided burden)
            </span>
          </div>
        )}
      </div>
    </FigureFrame>
  )
}
